import type { FlagsClient, FlagVariant } from "./index";

/**
 * Feature-flag overrides for testing flagged work before the flag exists in PostHog (or without
 * PostHog at all). Inert when NEXT_PUBLIC_MODE is "production", so local release builds and the
 * test environment can use them and production never does.
 *
 * Two sources, the later one winning per key:
 * - `NEXT_PUBLIC_LOCAL_FLAGS` in `.env.local`: `dark_mode_enabled:on,landing_v4_variant:test`
 * - `localStorage["tixu_flag_overrides"]`: `{"dark_mode_enabled":"on"}` (set it in DevTools, reload)
 *
 * Values are variant strings: booleans are "on" / "off" like PostHog's.
 */
export const LOCAL_FLAGS_STORAGE_KEY = "tixu_flag_overrides";

const isDevelopment = () => process.env.NEXT_PUBLIC_MODE !== "production";

const parseEnv = (raw: string | undefined): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const pair of (raw ?? "").split(",")) {
        const [key, value] = pair.split(":").map((s) => s.trim());
        if (key) out[key] = value || "on";
    }
    return out;
};

const parseStorage = (): Record<string, string> => {
    if (typeof window === "undefined") return {};
    try {
        const parsed = JSON.parse(window.localStorage.getItem(LOCAL_FLAGS_STORAGE_KEY) ?? "{}");
        if (!parsed || typeof parsed !== "object") return {};
        const out: Record<string, string> = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            out[key] = value === true ? "on" : value === false ? "off" : String(value);
        }
        return out;
    } catch {
        return {};
    }
};

export const readLocalFlagOverrides = (): Record<string, string> => {
    if (!isDevelopment()) return {};
    return { ...parseEnv(process.env.NEXT_PUBLIC_LOCAL_FLAGS), ...parseStorage() };
};

const toVariant = (key: string, value: string): FlagVariant => ({ value, key: value, payload: undefined });

/**
 * Wraps the real client so overridden keys answer locally; with no client (PostHog not
 * configured) and overrides present, a local-only client stands in. Returns the input untouched
 * in production or when nothing is overridden.
 */
export const withLocalFlagOverrides = (client: FlagsClient | null): FlagsClient | null => {
    const overrides = readLocalFlagOverrides();
    const keys = Object.keys(overrides);
    if (keys.length === 0) return client;

    if (typeof console !== "undefined") {
        console.info(`[flags] local overrides active: ${keys.map((k) => `${k}=${overrides[k]}`).join(", ")}`);
    }

    return {
        provider: client?.provider ?? "posthog",
        fetch: () => (client ? client.fetch() : Promise.resolve()),
        variant: (key) => (key in overrides ? toVariant(key, overrides[key]) : client ? client.variant(key) : {}),
        all: () => {
            const base = client ? client.all() : {};
            const local = Object.fromEntries(keys.map((key) => [key, toVariant(key, overrides[key])]));
            return { ...base, ...local };
        }
    };
};
